/**
 *  Proyectos (proyectos/*) y propietarios (propietarios/*) → /api/v1/projects,
 *  /api/v1/units y /api/v1/owners.
 *
 *  Formas viejas:
 *   - proyecto: {IdProyecto, Nombre, Direccion, Ciudad, CP, Ubicacion, NoUnidades}
 *   - tipo de unidad: {IdTipoUnidad, Nombre, Observaciones, Metros, Plano,
 *     TipoUnidadAreas: '[{IdTipoUnidadArea, IdTipoUnidad, IdArea}]',
 *     TipoUnidadEquipos: '[{IdTipoUnidadEquipo, IdTipoUnidadArea, IdEquipo}]'}
 *     (los dos árboles viajan como texto JSON; la página hace JSON.parse)
 *   - unidad: {IdUnidad, Numero, IdTipoUnidad, NombreUnidad, Observaciones, IdProyecto}
 *   - propietario: una fila por contrato (spWeb_PropietariosGet)
 */
import api, { schemaMessage } from '../apiV1';
import { list, ymd } from './index';

// errorOf prefiere el detalle por campo de un 400 al mensaje genérico.
const errorOf = (res) => ({
   error: true,
   message: (res.schema && schemaMessage(res.schema)) || res.message || 'Ocurrió un error, intenta de nuevo.',
});
const ok = (res) => (res.ok ? { response: true } : errorOf(res));

// Las rutas v1 de tipos y unidades llevan el proyecto; las llamadas viejas de
// editar/borrar solo mandan el id del tipo o de la unidad. La página siempre
// lista antes de editar, así que aquí se recuerda de qué proyecto es cada uno.
const typeProject = {};
const unitProject = {};

async function projectOfUnit(unitId) {
   if (unitProject[unitId]) return unitProject[unitId];
   const res = await api.get('units');
   if (res.ok) (res.data || []).forEach((u) => (unitProject[u.id] = u.projectId));
   return unitProject[unitId];
}

const missing = { error: true, message: 'Recarga la página e intenta de nuevo.' };

// --- proyectos ---

const toProject = (p) => ({
   IdProyecto: p.id,
   Nombre: p.name,
   Direccion: p.address,
   Ciudad: p.city,
   CP: p.zipCode,
   Ubicacion: p.location,
   NoUnidades: p.unitCount,
});

const projectBody = (d) => ({
   name: (d.Nombre || '').trim(),
   address: d.Direccion || '',
   city: d.Ciudad || '',
   zipCode: String(d.CP || '').trim(),
   location: d.Ubicacion || '',
   unitCount: d.NoUnidades == null ? '' : String(d.NoUnidades),
});

// --- tipos de unidad ---

function toUnitType(t, projectId) {
   typeProject[t.id] = projectId;
   const areas = [];
   const equipment = [];
   (t.areas || []).forEach((a) => {
      areas.push({ IdTipoUnidadArea: a.id, IdTipoUnidad: t.id, IdArea: a.areaId });
      (a.equipment || []).forEach((e) =>
         equipment.push({ IdTipoUnidadEquipo: e.id, IdTipoUnidadArea: a.id, IdEquipo: e.equipmentId })
      );
   });
   return {
      IdTipoUnidad: t.id,
      Nombre: t.name,
      Observaciones: t.notes,
      Metros: t.squareMeters,
      Plano: t.floorPlan,
      IdProyecto: projectId,
      TipoUnidadAreas: JSON.stringify(areas),
      TipoUnidadEquipos: JSON.stringify(equipment),
   };
}

const parse = (v) => {
   if (!v) return [];
   if (Array.isArray(v)) return v;
   try {
      return JSON.parse(v);
   } catch (e) {
      return [];
   }
};

// unitTypeBody arma el árbol completo de v1. Las filas nuevas de la vista
// traen LineaNueva = 1 y un IdTipoUnidadArea/IdTipoUnidadEquipo aleatorio que
// solo sirve para ligar equipos con su área; en v1 van con id 0. Los equipos
// de un área que se quitó se descartan (v1 los borra con el área).
function unitTypeBody(d) {
   const areas = parse(d.TipoUnidadAreas);
   const equipment = parse(d.TipoUnidadEquipos);
   return {
      name: (d.Nombre || '').trim(),
      notes: d.Observaciones || '',
      squareMeters: d.Metros == null ? '' : String(d.Metros),
      floorPlan: d.Plano || '',
      areas: areas.map((a) => ({
         id: a.LineaNueva ? 0 : Number(a.IdTipoUnidadArea) || 0,
         areaId: Number(a.IdArea),
         equipment: equipment
            // eslint-disable-next-line eqeqeq
            .filter((e) => e.IdTipoUnidadArea == a.IdTipoUnidadArea)
            .map((e) => ({
               id: e.LineaNueva ? 0 : Number(e.IdTipoUnidadEquipo) || 0,
               equipmentId: Number(e.IdEquipo),
            })),
      })),
   };
}

// --- unidades ---

function toUnit(u) {
   unitProject[u.id] = u.projectId;
   return {
      IdUnidad: u.id,
      Numero: u.number,
      IdTipoUnidad: u.unitTypeId,
      NombreUnidad: u.unitTypeName,
      Observaciones: u.notes,
      IdProyecto: u.projectId,
      Status: u.active === false ? 0 : 1,
   };
}

const unitBody = (d) => ({
   number: String(d.Numero || '').trim(),
   unitTypeId: Number(d.IdTipoUnidad),
   notes: d.Observaciones || '',
});

// --- propietarios ---

const toOwnership = (o) => ({
   IdPersona: o.personId,
   IdContrato: o.contractId,
   Nombre: o.firstName,
   Apellidos: o.lastName,
   Email: o.email,
   Telefono: o.phone,
   IdUnidad: o.unitId,
   Numero: o.unitNumber,
   IdProyecto: o.projectId,
   NombreProyecto: o.projectName,
   NumeroContrato: o.contractNumber,
   MesesGarantia: o.warrantyMonths,
   Comentarios: o.notes,
   FechaCompra: o.purchaseDate,
   FechaEntrega: o.deliveryDate,
});

const contractBody = (d) => ({
   firstName: (d.Nombre || '').trim(),
   lastName: (d.Apellidos || '').trim(),
   phone: d.Telefono || '',
   unitId: Number(d.IdUnidad) || 0,
   contractNumber: String(d.NumeroContrato || '').trim(),
   purchaseDate: ymd(d.FechaCompra),
   deliveryDate: ymd(d.FechaEntrega),
   warrantyMonths: Number(d.MesesGarantia) || 0,
   notes: d.Comentarios || '',
});

export default {
   // proyectos
   'GET proyectos/get': async () => {
      const res = await api.get('projects');
      if (!res.ok) return errorOf(res);
      return list((res.data || []).map(toProject));
   },
   'POST proyectos/create': async (d) => ok(await api.post('projects', projectBody(d))),
   'POST proyectos/update': async (d) => ok(await api.put(`projects/${d.IdProyecto}`, projectBody(d))),
   'POST proyectos/delete': async (d) => ok(await api.del(`projects/${d.IdProyecto}`)),

   // tipos de unidad
   'GET proyectos/get/tipos/unidades': async (d) => {
      const res = await api.get(`projects/${d.IdProyecto}/unit-types`);
      if (!res.ok) return errorOf(res);
      return list((res.data || []).map((t) => toUnitType(t, Number(d.IdProyecto))));
   },
   'POST proyectos/create/tipo/unidad': async (d) =>
      ok(await api.post(`projects/${d.IdProyecto}/unit-types`, unitTypeBody(d))),
   'POST proyectos/update/tipo/unidad': async (d) => {
      const projectId = d.IdProyecto || typeProject[d.IdTipoUnidad];
      if (!projectId) return missing;
      return ok(await api.put(`projects/${projectId}/unit-types/${d.IdTipoUnidad}`, unitTypeBody(d)));
   },
   'POST proyectos/delete/tipo/unidad': async (d) => {
      const projectId = d.IdProyecto || typeProject[d.IdTipoUnidad];
      if (!projectId) return missing;
      return ok(await api.del(`projects/${projectId}/unit-types/${d.IdTipoUnidad}`));
   },

   // unidades
   'GET proyectos/get/unidades': async (d) => {
      const res = await api.get(`projects/${d.IdProyecto}/units`);
      if (!res.ok) return errorOf(res);
      return list((res.data || []).map(toUnit));
   },
   // Incluye las dadas de baja, como Koa: garantías busca aquí el número de
   // la unidad de cada solicitud, aunque ya no esté activa.
   'GET proyectos/get/all/unidades': async () => {
      const res = await api.get('units', { includeInactive: 'true' });
      if (!res.ok) return errorOf(res);
      return list((res.data || []).map(toUnit));
   },
   'POST proyectos/create/unidad': async (d) => ok(await api.post(`projects/${d.IdProyecto}/units`, unitBody(d))),
   'POST proyectos/update/unidad': async (d) => {
      const projectId = d.IdProyecto || (await projectOfUnit(d.IdUnidad));
      if (!projectId) return missing;
      return ok(await api.put(`projects/${projectId}/units/${d.IdUnidad}`, unitBody(d)));
   },
   'POST proyectos/delete/unidad': async (d) => {
      const projectId = d.IdProyecto || (await projectOfUnit(d.IdUnidad));
      if (!projectId) return missing;
      return ok(await api.del(`projects/${projectId}/units/${d.IdUnidad}`));
   },

   // propietarios
   'GET propietarios/get': async () => {
      const res = await api.get('owners');
      if (!res.ok) return errorOf(res);
      return list((res.data || []).map(toOwnership));
   },
   // Selector "Nombre Apellidos (101)" de dashboard, garantías y notificaciones.
   'GET propietarios/get/all': async () => {
      const res = await api.get('owners/options');
      if (!res.ok) return errorOf(res);
      return list((res.data || []).map((o) => ({ IdPersona: o.personId, Nombre: o.label, IdUnidad: o.unitId })));
   },
   'POST propietarios/create': async (d) => {
      const res = await api.post('owners', { email: (d.Email || '').trim(), ...contractBody(d) });
      if (res.ok) return { created: true, message: '' };
      return { created: false, ...errorOf(res) };
   },
   // Status 0 es la baja del contrato. El correo no se cambia aquí (v1 no lo
   // permite: el SP lo usa como llave).
   'POST propietarios/update': async (d) => {
      const path = `owners/${d.IdPersona}/contracts/${d.IdContrato}`;
      // eslint-disable-next-line eqeqeq
      if (d.Status == 0) return ok(await api.del(path));
      return ok(await api.put(path, contractBody(d)));
   },
};
